import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Check if we're on the homepage
  const isHomepage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle navigation to homepage sections
  useEffect(() => {
    if (location.pathname === '/' && location.hash) {
      // Small delay to ensure the page has loaded
      setTimeout(() => {
        const targetElement = document.querySelector(location.hash);
        if (targetElement) {
          const headerOffset = 100;
          const elementPosition = targetElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location]);

  // Close mobile menu when window is resized to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
        setIsServicesDropdownOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSmoothScroll = (e, targetId) => {
    e.preventDefault();
    
    // Close mobile menu
    setIsMobileMenuOpen(false);
    setIsServicesDropdownOpen(false);
    
    if (location.pathname === '/') {
      // We're on homepage, smooth scroll to section
      const targetElement = document.querySelector(targetId);
      if (targetElement) {
        const headerOffset = 100;
        const elementPosition = targetElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    } else {
      // We're on a different page, navigate to homepage with hash
      navigate(`/${targetId}`);
    }
  };

  const services = [
    { name: 'Driveway Washing', path: '/services/driveway-washing' },
    { name: 'Patio Cleaning', path: '/services/patio-cleaning' },
    { name: 'Gutter Cleaning', path: '/services/gutter-cleaning' },
    { name: 'Roof Cleaning', path: '/services/roof-cleaning' },
    { name: 'Solar Panel Cleaning', path: '/services/solar-panel-cleaning' },
    { name: 'Residential and Commercial Cleaning', path: '/services/residential-cleaning' }
  ];

  const handleServicesMouseEnter = () => {
    if (window.innerWidth > 768) {
      setIsServicesDropdownOpen(true);
    }
  };

  const handleServicesMouseLeave = () => {
    if (window.innerWidth > 768) {
      setIsServicesDropdownOpen(false);
    }
  };

  const handleServicesClick = (e) => {
    e.preventDefault();
    // Toggle dropdown on mobile, or navigate on desktop if it's a link
    if (window.innerWidth <= 768) {
      setIsServicesDropdownOpen(!isServicesDropdownOpen);
    }
  };

  const closeAllMenus = () => {
    setIsServicesDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  const handleHomeClick = (e) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    setIsServicesDropdownOpen(false);
    
    if (location.pathname === '/') {
      // Already on homepage, scroll to top
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      // Navigate to homepage
      navigate('/');
    }
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // Close services dropdown when closing mobile menu
    if (isMobileMenuOpen) {
      setIsServicesDropdownOpen(false);
    }
  };

  const handleServiceLinkClick = () => {
    // Close all menus when a service link is clicked
    closeAllMenus();
  };

  // Create header class names
  const headerClasses = [
    isScrolled ? 'scrolled' : '',
    isHomepage ? 'homepage' : 'other-page'
  ].filter(Boolean).join(' ');

  return (
    <header id="header" className={headerClasses}>
      <div className="logo-container">
        <img src="/images/logoFinalb.png" alt="Cleaning Logo" className="header-logo-img" />
        <div className="logo-text-container">
          <div className="logo-with-socials">
            <Link to="/" className="logo">The Clean Knight Co.</Link>
            <div className="header-social-links">
              <a 
                  href="https://www.facebook.com/profile.php?id=61578825262959" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="social-icon"
              >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
              </a>
              <a 
                  href="https://www.instagram.com/the_clean_knight?igsh=MWg2ZG55eTJkODk0OA%3D%3D&utm_source=qr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="social-icon"
              >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1 1 12.324 0 6.162 6.162 0 0 1-12.324 0zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm4.965-10.405a1.44 1.44 0 1 1 2.881.001 1.44 1.44 0 0 1-2.881-.001z"/>
                  </svg>
              </a>
              </div>
            </div>
            <span className="logo-tagline">Exterior Cleaning Services</span>
        </div>
      </div>
      
      <nav>
        <ul id="nav-menu" className={isMobileMenuOpen ? 'show' : ''}>
          <li>
            <a href="/" onClick={handleHomeClick}>Home</a>
          </li>
          
          <li 
            className={`services-dropdown ${isServicesDropdownOpen ? 'active' : ''}`}
            onMouseEnter={handleServicesMouseEnter}
            onMouseLeave={handleServicesMouseLeave}
          >
            <span 
              className="dropdown-trigger"
              onClick={handleServicesClick}
            >
              Services <span className="dropdown-arrow">▼</span>
            </span>
            <ul className={`dropdown-menu ${isServicesDropdownOpen ? 'show' : ''}`}>
              {services.map((service, index) => (
                <li key={index}>
                  <Link to={service.path} onClick={handleServiceLinkClick}>
                    {service.name}
                  </Link>
                </li>
              ))}
              <li>
                <a href="/#services" onClick={(e) => { 
                  handleSmoothScroll(e, '#services'); 
                }}>
                  All Services
                </a>
              </li>
            </ul>
          </li>
          
          <li>
            <a href="/#about" onClick={(e) => handleSmoothScroll(e, '#about')}>About Us</a>
          </li>
          <li>
            <Link to="/blog" onClick={closeAllMenus}>Blog</Link>
          </li>
          <li>
            <a href="/#testimonials" onClick={(e) => handleSmoothScroll(e, '#testimonials')}>Testimonials</a>
          </li>
          <li>
            <a href="/#faq" onClick={(e) => handleSmoothScroll(e, '#faq')}>FAQ</a>
          </li>
          <li>
            <a href="/#contact" onClick={(e) => handleSmoothScroll(e, '#contact')}>Contact</a>
          </li>
        </ul>
        
        <div 
          className="mobile-menu-btn" 
          onClick={handleMobileMenuToggle}
        >
          ☰
        </div>
      </nav>
    </header>
  );
};

export default Header;